import { useMemo, useRef } from "react";

/**
 * Maps rawData through mapFn, but if a fetch transiently comes back empty
 * (e.g. a flaky refetch) it keeps showing the last non-empty result for the
 * same cacheKey instead of flashing an empty/"no data" state. Changing
 * cacheKey (e.g. switching hospital) clears the cache so stale data isn't
 * shown for a different context.
 */
export default function useStableChartData<T, R>(
  rawData: T[] | undefined | null,
  mapFn: (item: T) => R,
  cacheKey: string | undefined
): R[] {
  const lastNonEmptyRef = useRef<{ cacheKey?: string; data: R[] }>({
    data: [],
  });

  return useMemo(() => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return lastNonEmptyRef.current.cacheKey === cacheKey
        ? lastNonEmptyRef.current.data
        : [];
    }

    const mapped = rawData.map(mapFn);
    lastNonEmptyRef.current = { cacheKey, data: mapped };
    return mapped;
  }, [rawData, cacheKey, mapFn]);
}
